import { css } from '@emotion/css';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ReactComponent as Loudspeaker } from '../../assets/images/loudspeaker.svg';
import { sendEvent } from '../../lib/google-analytics';
import { groupBy, throttle } from '../../lib/utils';
import { FriendItem } from '../../models/FriendItem';
import { FancyButton } from '../shared/buttons/FancyButton';
import { FriendListItem } from './FriendListItem';
import { OnlinePulse } from './OnlinePulse';

interface BestFriendsContentProps {
  friendItems: FriendItem[];
}

const bestFriendsContentStyles = css`
  max-width: 480px;
  color: #938666;
  margin: 0 auto;
  padding: 0 0 64px;

  .control {
    display: block;
    width: 100%;
    margin: 24px 0 0;
    padding: 0 20px;
  }
  .send {
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
    font-size: 14px;
    width: 100%;
    min-height: 56px;
    padding: 0 8px;
  }
  .send svg {
    flex: 0 0 auto;
  }
  .send span {
    flex: 1 1 auto;
    white-space: normal;
    padding: 0 2px;
  }
  .online-sec,
  .offline-sec {
    margin: 24px 0;

    header {
      display: inline-block;
      color: #938666;
      font-size: 12px;
      margin: 4px 20px;
    }
  }
  .pict {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 4px 0 0;
  }
  .empty {
    text-align: center;
    margin: 8px 24px 32px;
    padding: 24px 0;
    border-bottom: 2px dashed #dcd8bf;
  }
  .deco {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 24px;
    border-radius: 12px;
    padding: 0 10px;
  }
  .online-sec {
    .deco {
      color: #bff4c4;
      background: #50b163;
    }
  }
  .offline-sec {
    .deco {
      color: #bdbcaf;
      background: #a3a299;
    }
  }
`;

export const BestFriendsContent: React.FC<BestFriendsContentProps> = ({ friendItems }) => {
  const { t } = useTranslation('best_friends');
  const history = useHistory();
  const handleSendButtonClick = useCallback(
    throttle(() => {
      history.push('/players/best-friends-message-to-all');
      sendEvent('best_friends_top_message_to_all', 'click');
    }, 1000),
    [history]
  );

  const grouped = groupBy(friendItems, f => (f.isOnline ? 'online' : 'offline'));
  const online = grouped.online || [];
  const offline = grouped.offline || [];

  return (
    <div className={bestFriendsContentStyles}>
      <div className="control">
        <FancyButton background="#fb984a" color="white" onClick={handleSendButtonClick}>
          <div className="send">
            <Loudspeaker />
            <span>{t('button_message_to_all')}</span>
          </div>
        </FancyButton>
      </div>

      <section className="online-sec">
        <header>
          <span className="deco">
            <span className="pict">
              <OnlinePulse on={online.length > 0} />
            </span>
            {t('online')}
          </span>
        </header>
        {online.length === 0 ? (
          <div className="empty">{t('empty_online')}</div>
        ) : (
          <ul
            className={css`
              margin: 4px 0;
            `}
          >
            {online.map(friend => (
              <FriendListItem key={friend.id} {...friend} />
            ))}
          </ul>
        )}
      </section>

      <section className="offline-sec">
        <header>
          <span className="deco">{t('offline')}</span>
        </header>
        {offline.length === 0 ? (
          <div className="empty">{t('empty_offline')}</div>
        ) : (
          <ul
            className={css`
              margin: 4px 0;
            `}
          >
            {offline.map(friend => (
              <FriendListItem key={friend.id} {...friend} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
